import { useQuery } from '@apollo/react-hooks';
import Button from '@atlaskit/button';
import DynamicTable from '@atlaskit/dynamic-table';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { WIDGETS_QUERY } from '../../graphql/queries';
import Loading from '../common/Loading';

const Empty = styled.div`
    h3,
    p {
        margin-bottom: 14px;
    }
    img {
        width: 160px;
        margin-top: 14px;
    }
`;

function getTableHead() {
    return {
        cells: [
            {
                key: 'name',
                content: 'Widget name',
                isSortable: true,
            },
            {
                key: 'description',
                content: 'Description',
            },
            {
                key: 'action',
                content: '',
                width: 20,
            },
        ],
    };
}

function getTableRows({ widgets, openWidgetDetails }) {
    const rows = [];
    widgets.forEach((widget) => {
        const { id, name, description } = widget;
        rows.push({
            key: id,
            cells: [
                {
                    key: name,
                    content: name,
                },
                {
                    key: 'description',
                    content: <p>{description}</p>,
                },
                {
                    content: (
                        <p
                            style={{
                                cursor: 'pointer',
                                color: '#0052CC',
                                WebkitLineClamp: 1,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                            }}
                            onClick={() => {
                                openWidgetDetails(widget);
                            }}
                        >
                            {`Widget details >`}
                        </p>
                    ),
                },
            ],
        });
    });
    return rows;
}

function EmptyView({ openCreateWidgetModal }) {
    const message = 'Create your first notification widget';
    const subMessage = 'Once you create the widget here you can embed it in your application.';
    return (
        <Empty>
            <img alt="no results" src="/no-widgets.svg" />
            <h3>{message}</h3>
            <p>{subMessage}</p>
            <Button appearance="primary" style={{ marginTop: '20px' }} onClick={openCreateWidgetModal}>
                {`Let's do it`}
            </Button>
        </Empty>
    );
}

const NoWidgetPermission = () => {
    return (
        <Empty>
            <img alt="no results" src="/no-widgets.svg" />
            <h3>{'You don’t have access to any widgets'}</h3>
            <p>{'Reach out to your Jira Administrator to get editor permission to widgets you need.'}</p>
        </Empty>
    );
};

function WidgetsTable({ openCreateWidgetModal, openWidgetDetails, refetchData, displayNoPermissionPanel }) {
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({ key: 'name', order: 'ASC' });

    const AVAILABLE_HEIGHT = window.innerHeight - 280;
    const ROW_HEIGHT = 61;

    const { data: widgetsData, loading: widgetsLoading, refetch } = useQuery(WIDGETS_QUERY);

    useEffect(() => {
        if (refetchData) {
            refetch();
        }
    }, [refetch, refetchData]);

    if (widgetsLoading) {
        return <Loading />;
    }

    return (
        <DynamicTable
            emptyView={
                displayNoPermissionPanel ? (
                    <NoWidgetPermission />
                ) : (
                    <EmptyView openCreateWidgetModal={openCreateWidgetModal} />
                )
            }
            head={getTableHead()}
            rows={getTableRows({
                widgets: (widgetsData || {}).widgets || [],
                openWidgetDetails,
            })}
            rowsPerPage={Math.floor(AVAILABLE_HEIGHT / ROW_HEIGHT)}
            isLoading={false}
            defaultSortKey="name"
            defaultSortOrder="ASC"
            sortKey={sort.key}
            sortOrder={sort.order}
            isFixedSize
            page={page}
            onSetPage={(newPage) => setPage(newPage)}
            onSort={(data) => {
                setSort({ key: data.key, order: data.sortOrder });
            }}
        />
    );
}

export default WidgetsTable;
